import { useCallback, useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "@/components/AppShell";
import { ADMIN_NAV } from "./nav";
import { documentService } from "@/services/documentService";
import { errorMessage } from "@/services/apiClient";
import { fmtDate } from "@/lib/format";
import { Pagination } from "@/components/Pagination";

const FILTERS = [
  { key: "pending", label: "Pendentes" },
  { key: "approved", label: "Aprovados" },
  { key: "rejected", label: "Recusados" },
  { key: "", label: "Todos" },
];
const KIND = { id_card: "ID", passport: "Passaporte", address_proof: "Comprovativo de residência", other: "Outro" };
const TONE = { pending: "text-[#F59E0B]", approved: "text-[#10B981]", rejected: "text-[#EF4444]" };

export default function AdminDocumentsPage() {
  const [filter, setFilter] = useState("pending");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pending: 0 });
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    documentService
      .adminList({ page, per_page: 10, ...(filter ? { status: filter } : {}) })
      .then(setData)
      .catch(() => setData({ items: [], total: 0, pending: 0 }));
  }, [filter, page]);

  useEffect(() => { load(); }, [load]);

  const review = async (doc, status) => {
    setBusy(doc.id);
    try {
      const note = status === "rejected" ? window.prompt("Motivo da recusa (opcional)") || "" : "";
      await documentService.review(doc.id, status, note);
      toast.success(status === "approved" ? "Documento aprovado" : "Documento recusado");
      load();
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy("");
    }
  };

  return (
    <AppShell nav={ADMIN_NAV} title="Documentos" subtitle={`${data.pending} pendente(s) de aprovação`}>
      <div data-testid="admin-documents-page" className="space-y-6">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key || "all"}
              data-testid={`documents-filter-${f.key || "all"}`}
              onClick={() => { setFilter(f.key); setPage(1); }}
              className={`rounded-md border px-4 py-2 text-xs uppercase tracking-[0.15em] transition-colors ${
                filter === f.key ? "border-[#2563EB] bg-[#2563EB]/15 text-white" : "border-[#22252F] text-[#94A3B8] hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="overflow-x-auto rounded-md border border-[#22252F] bg-[#12141A]">
          <table className="w-full min-w-[860px] text-left text-sm">
            <thead className="border-b border-[#22252F] text-[11px] uppercase tracking-[0.2em] text-[#94A3B8]">
              <tr>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Tipo</th>
                <th className="px-4 py-3">Documento</th>
                <th className="px-4 py-3">Enviado</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody data-testid="admin-documents-body">
              {data.items.map((d) => (
                <tr key={d.id} data-testid={`admin-document-${d.id}`} className="border-b border-[#1A1D25]">
                  <td className="px-4 py-3">
                    <p className="text-white">{d.user_name || "—"}</p>
                    <p className="font-mono text-[10px] text-[#64748B]">{d.user_email}</p>
                  </td>
                  <td className="px-4 py-3 text-[#94A3B8]">{KIND[d.kind]}</td>
                  <td className="px-4 py-3">
                    <p className="text-[#C7D0DE]">{d.document_number || "—"}</p>
                    {d.note ? <p className="mt-1 text-xs text-[#94A3B8]">{d.note}</p> : null}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-[#94A3B8]">{fmtDate(d.created_at)}</td>
                  <td className={`px-4 py-3 font-mono text-xs uppercase ${TONE[d.status]}`}>{d.status}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button data-testid={`approve-document-${d.id}`} onClick={() => review(d, "approved")} disabled={busy === d.id}
                              className="rounded-md border border-[#22252F] p-2 text-[#10B981] transition-colors hover:border-[#10B981]/50">
                        <Check className="h-4 w-4" />
                      </button>
                      <button data-testid={`reject-document-${d.id}`} onClick={() => review(d, "rejected")} disabled={busy === d.id}
                              className="rounded-md border border-[#22252F] p-2 text-[#EF4444] transition-colors hover:border-[#EF4444]/50">
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {data.items.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-[#64748B]">Sem documentos</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          pages={Math.max(1, Math.ceil(data.total / 10))}
          total={data.total}
          perPage={10}
          onPage={setPage}
          onPerPage={() => {}}
          testId="documents-pagination"
        />
      </div>
    </AppShell>
  );
}
